import { getActiveStepper, registerDateNavSync, setActiveStepper } from './shared';

/**
 * Shared date-nav controller (Phase 262, HOME-01/02) — owns the prev/next/picker handling for
 * the single `#chart-edit-modal` header control that all five click-to-edit modals inherit
 * (modal.ts, gym-modal.ts, runs-modal.ts, stairmaster-modal.ts, mood-modal.ts).
 *
 * This module never imports a modal module. It dispatches exclusively through the active-
 * stepper registry on `shared.ts` (`getActiveStepper()`/`setActiveStepper()`), which each modal
 * populates itself on open — see `shared.ts`'s registry docblock for the full rationale.
 *
 * Locked decisions this module implements:
 * - P-02: stepping the date is navigation, never a merge. No confirm dialog, no auto-save.
 * - P-03: forward dates are capped at today, enforced here (the next-arrow's disabled state)
 *   as the SECOND enforcement point — `picker.ts`'s `data-max-date="today"` is the first, but a
 *   `maxDate` alone still lets an arrow tap increment the date state past today.
 * - P-04: a step blanks the modal body via the owning modal's own step implementation, then
 *   repopulates it — this module owns only the in-flight lockout and the one failure-copy path
 *   (T-262-21/T-262-22), not the body-reload mechanics themselves.
 */

/** The exact P-03/T-262-19 date-only shape every step must satisfy before it is ever sent on. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** UI-SPEC Copywriting Contract — the one failure-copy path (T-262-21's watchdog + a real error both land here). */
const STEP_FAILURE_MESSAGE = "Couldn't load this date. Try again.";

/** A dropped response must not leave the date-nav controls permanently disabled (T-262-21). */
const STEP_WATCHDOG_MS = 10000;

let _initialized = false;
let _busy = false;
let _capDisabled = false; // true once the currently-synced date is >= today (P-03)
let _watchdogId: number | null = null;

function todayYmd(): string {
    return formatYmd(new Date());
}

// Local getters only — never the UTC-shifting whole-timestamp string conversion this repo has
// already been bitten by, which would disable/enable the next arrow up to a day early or late
// depending on the hour (this repo's own documented trap).
function formatYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// Local-midnight Date construction + setDate() is DST-safe and handles month/year rollover —
// deliberately not millisecond arithmetic.
function addDays(date: string, delta: number): string {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    return formatYmd(d);
}

function getPrevBtn(): HTMLButtonElement | null {
    return document.getElementById('chart-edit-modal-prev') as HTMLButtonElement | null;
}

function getNextBtn(): HTMLButtonElement | null {
    return document.getElementById('chart-edit-modal-next') as HTMLButtonElement | null;
}

function getDateInput(): HTMLInputElement | null {
    return document.getElementById('chart-edit-modal-date-input') as HTMLInputElement | null;
}

/** Next-arrow disabled state is the OR of the P-03 cap and the in-flight lockout — computed
 *  independently of call order so a header sync during a busy step can never re-enable it. */
function applyNextButtonState(): void {
    const btn = getNextBtn();
    if (!btn) return;
    const disabled = _busy || _capDisabled;
    btn.disabled = disabled;
    if (disabled) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
}

function setControlsBusy(busy: boolean): void {
    _busy = busy;
    const prevBtn = getPrevBtn();
    const input = getDateInput();
    if (prevBtn) prevBtn.disabled = busy;
    if (input) input.disabled = busy;
    applyNextButtonState();
}

function clearInlineError(): void {
    document.getElementById('chart-edit-modal-body')?.querySelector('.modal-inline-error')?.remove();
}

function insertInlineError(): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) return;
    const errorEl = document.createElement('div');
    errorEl.className = 'text-danger small mt-2 modal-inline-error';
    errorEl.textContent = STEP_FAILURE_MESSAGE; // textContent only — T-262-22, never innerHTML
    body.appendChild(errorEl);
}

/**
 * Registered with shared.ts as the date-nav sync hook — called by setActiveStepper() every time
 * a modal opens or a step completes, so the header reflects the registry's date with zero work
 * required from any modal module.
 */
function syncDateNav(date: string): void {
    if (!date) return; // registry teardown (setActiveStepper('', null)) — nothing to sync

    const input = getDateInput();
    if (input) {
        if ((input as any)._flatpickr) {
            // false suppresses flatpickr's own change event, preventing a feedback loop back
            // into bindDateInputChange()'s 'change' listener below.
            (input as any)._flatpickr.setDate(date, false);
        } else {
            input.value = date;
        }
    }

    _capDisabled = date >= todayYmd();
    applyNextButtonState();
}

/**
 * The one step path prev/next/picker all funnel into (P-04's shared entry point). Reads the
 * active stepper from the registry, clamps/locks per P-03/T-262-21, updates the registry BEFORE
 * the fetch resolves (the exact `modal.ts:102` stale-state failure mode, arriving via a new
 * route), then delegates to the modal's own step implementation.
 */
function stepTo(newDate: string): void {
    const current = getActiveStepper();
    if (!current) return; // no modal open — nothing to step

    if (newDate > todayYmd()) return; // P-03's second enforcement point
    if (_busy) return; // T-262-21: reject a re-entrant step while one is in flight

    clearInlineError();
    setControlsBusy(true);

    // Registry + header updated to the new date before the fetch resolves (T-262-20).
    setActiveStepper(newDate, current.step);

    let settled = false;
    const done = (ok: boolean) => {
        if (settled) return; // watchdog vs. real callback — first one wins
        settled = true;
        if (_watchdogId !== null) {
            window.clearTimeout(_watchdogId);
            _watchdogId = null;
        }
        setControlsBusy(false);
        if (!ok) insertInlineError();
    };

    _watchdogId = window.setTimeout(() => done(false), STEP_WATCHDOG_MS);

    current.step(newDate, done);
}

function bindPrevNext(): void {
    getPrevBtn()?.addEventListener('click', () => {
        const current = getActiveStepper();
        if (!current) return;
        stepTo(addDays(current.date, -1));
    });
    getNextBtn()?.addEventListener('click', () => {
        const current = getActiveStepper();
        if (!current) return;
        stepTo(addDays(current.date, 1));
    });
}

function bindDateInputChange(input: HTMLInputElement): void {
    input.addEventListener('change', () => {
        const current = getActiveStepper();
        if (!current) return;

        const value = input.value.trim();
        if (!DATE_RE.test(value)) {
            // allowInput:true means a user can type anything — resync to the registry's
            // current date rather than stepping on a malformed value (T-262-19).
            syncDateNav(current.date);
            return;
        }
        if (value === current.date) return; // no-op re-selection of the already-active date
        stepTo(value);
    });
}

function bindModalTeardown(modalEl: HTMLElement): void {
    modalEl.addEventListener('hidden.bs.modal', () => {
        // Clears the registry so a stale stepper from a previously-open modal can never fire
        // (T-262-20), and releases a busy lockout left behind by a step whose response never
        // arrived before the modal was dismissed.
        setActiveStepper('', null);
        if (_watchdogId !== null) {
            window.clearTimeout(_watchdogId);
            _watchdogId = null;
        }
        setControlsBusy(false);
        clearInlineError();
    });
}

/**
 * Binds the shared date-nav's prev/next/picker/teardown handlers exactly once. Inert (returns
 * immediately) on any page that doesn't render `#chart-edit-modal-date-input` — this repo has a
 * documented double-bind failure mode for shared components initialized on every page load.
 */
export function initDateNav(): void {
    if (_initialized) return;

    const input = getDateInput();
    if (!input) return; // non-home page — nothing to bind

    _initialized = true;

    registerDateNavSync(syncDateNav);
    bindPrevNext();
    bindDateInputChange(input);

    const modalEl = document.getElementById('chart-edit-modal');
    if (modalEl) bindModalTeardown(modalEl);
}
